import { type Ref, watch } from 'vue';
import { type PeriodKey, isValidPeriod } from './usePeriod';
import { type CalculatorKey, isValidMeasurement } from './useCalculator';
import { type ColorSchemeKey, isValidColorScheme } from './useColorScheme';
import { type SizeByKey, isValidSizeBy } from './useSizeBy';

// URL param keys — prefixed with "mc-" to avoid collisions with host page params
const URL_PARAMS = {
  measurement: 'mc-measurement',
  period: 'mc-period',
  sizeBy: 'mc-sizeby',
  colorScheme: 'mc-scheme',
  showInfoHover: 'mc-showinfohover',
  showMiniChart: 'mc-showminichart',
  autoRefresh: 'mc-autorefresh',
  showSymbols: 'mc-showsymbols',
  showSideTable: 'mc-showsidetable'
} as const;

interface UrlSyncDependencies {
  periodKey: Ref<PeriodKey>;
  calculatorKey: Ref<CalculatorKey>;
  colorSchemeKey: Ref<ColorSchemeKey>;
  sizeByKey: Ref<SizeByKey>;
  showSymbols: Ref<boolean>;
  isAutoRefreshEnabled: Ref<boolean>;
  isHeaderShown: Ref<boolean>;
  isSharpChartShown: Ref<boolean>;
  showSideTable: Ref<boolean>;
}

function setUrlParam(key: string, value: string | boolean): void {
  const url = new URL(globalThis.location.href);
  url.searchParams.set(key, String(value));
  globalThis.history.replaceState(null, '', url.toString());
}

function deleteUrlParam(key: string): void {
  const url = new URL(globalThis.location.href);
  url.searchParams.delete(key);
  globalThis.history.replaceState(null, '', url.toString());
}

function parseBoolean(raw: string | null): boolean | undefined {
  if (raw === null)
    return undefined;
  return raw === 'true';
}

function parseValidatedString<T extends string>(
  raw: string | null,
  isValid: (v: string) => v is T,
): T | undefined {
  if (raw === null)
    return undefined;
  return isValid(raw) ? raw : undefined;
}

// Bidirectional sync between the widget's reactive refs and URL search params.
// On creation, any valid URL params override the current ref values (which were
// set from HTML attributes or code defaults). Watchers then keep the URL in sync
// as the user changes settings. Params that match the instance's initial value
// (the "baseline") are removed from the URL to keep it clean.
export function createUrlSync(dependencies: UrlSyncDependencies, options: { enabled: boolean }) {
  if (!options.enabled)
    return;

  const params = new URLSearchParams(globalThis.location.search);

  // Capture initial values before URL override. These serve as the baseline:
  // when a ref returns to its initial value the URL param is removed.
  const initialPeriod = dependencies.periodKey.value;
  const initialCalculator = dependencies.calculatorKey.value;
  const initialColorScheme = dependencies.colorSchemeKey.value;
  const initialSizeBy = dependencies.sizeByKey.value;
  const initialShowSymbols = dependencies.showSymbols.value;
  const initialAutoRefresh = dependencies.isAutoRefreshEnabled.value;
  const initialShowInfoHover = dependencies.isHeaderShown.value;
  const initialShowMiniChart = dependencies.isSharpChartShown.value;
  const initialShowSideTable = dependencies.showSideTable.value;

  // --- Read from URL and override refs ---

  const urlPeriod = parseValidatedString(params.get(URL_PARAMS.period), isValidPeriod);
  if (urlPeriod !== undefined)
    dependencies.periodKey.value = urlPeriod;

  const urlCalculator = parseValidatedString(params.get(URL_PARAMS.measurement), isValidMeasurement);
  if (urlCalculator !== undefined)
    dependencies.calculatorKey.value = urlCalculator;

  const urlColorScheme = parseValidatedString(params.get(URL_PARAMS.colorScheme), isValidColorScheme);
  if (urlColorScheme !== undefined)
    dependencies.colorSchemeKey.value = urlColorScheme;

  const urlSizeBy = parseValidatedString(params.get(URL_PARAMS.sizeBy), isValidSizeBy);
  if (urlSizeBy !== undefined)
    dependencies.sizeByKey.value = urlSizeBy;

  const urlShowSymbols = parseBoolean(params.get(URL_PARAMS.showSymbols));
  if (urlShowSymbols !== undefined)
    dependencies.showSymbols.value = urlShowSymbols;

  const urlAutoRefresh = parseBoolean(params.get(URL_PARAMS.autoRefresh));
  if (urlAutoRefresh !== undefined)
    dependencies.isAutoRefreshEnabled.value = urlAutoRefresh;

  const urlShowInfoHover = parseBoolean(params.get(URL_PARAMS.showInfoHover));
  if (urlShowInfoHover !== undefined)
    dependencies.isHeaderShown.value = urlShowInfoHover;

  const urlShowMiniChart = parseBoolean(params.get(URL_PARAMS.showMiniChart));
  if (urlShowMiniChart !== undefined)
    dependencies.isSharpChartShown.value = urlShowMiniChart;

  const urlShowSideTable = parseBoolean(params.get(URL_PARAMS.showSideTable));
  if (urlShowSideTable !== undefined)
    dependencies.showSideTable.value = urlShowSideTable;

  // --- Write to URL on ref changes ---

  function syncRef<T extends string | boolean>(targetRef: Ref<T>, urlKey: string, initialValue: T) {
    watch(targetRef, (value) => {
      if (value === initialValue)
        deleteUrlParam(urlKey);
      else
        setUrlParam(urlKey, value);
    });
  }

  syncRef(dependencies.periodKey, URL_PARAMS.period, initialPeriod);
  syncRef(dependencies.calculatorKey, URL_PARAMS.measurement, initialCalculator);
  syncRef(dependencies.colorSchemeKey, URL_PARAMS.colorScheme, initialColorScheme);
  syncRef(dependencies.sizeByKey, URL_PARAMS.sizeBy, initialSizeBy);
  syncRef(dependencies.showSymbols, URL_PARAMS.showSymbols, initialShowSymbols);
  syncRef(dependencies.isAutoRefreshEnabled, URL_PARAMS.autoRefresh, initialAutoRefresh);
  syncRef(dependencies.isHeaderShown, URL_PARAMS.showInfoHover, initialShowInfoHover);
  syncRef(dependencies.isSharpChartShown, URL_PARAMS.showMiniChart, initialShowMiniChart);
  syncRef(dependencies.showSideTable, URL_PARAMS.showSideTable, initialShowSideTable);
}
